import {setInShallowClone} from '../../../util/utility';
import * as PanelTypes from '../panel/types';
import * as PanelBankSectionConfigTypes from '../panelBankSectionConfig/types';
import {ActionType, ViewReducerState} from '../reducerSupport';
import * as Actions from './actions';
import {
  getMovePanelData,
  getUndoData,
  handleMovePanelToDiffSection,
  handleMovePanelToSameSection,
} from './movePanelUtils';
import * as Types from './types';

export const undoMovePanelAction = (
  state: ViewReducerState,
  ref: Types.Ref,
  active: {
    panelRef: PanelTypes.Ref;
    prevIsPanelAuto: boolean | undefined;
    sectionRef: PanelBankSectionConfigTypes.Ref;
    prevSectionPanelsAuto: boolean | undefined;
  },
  target: {
    panelRef: PanelTypes.Ref | null; // a section can be empty, so the panelRef can be null
    sectionRef: PanelBankSectionConfigTypes.Ref;
    insertDir: 'before' | 'after'; // This is the direction the panel will be inserted in the target section
    prevSectionPanelsAuto: boolean | undefined;
  }
): [ViewReducerState, ActionType] => {
  const {
    activePanelIndex,
    targetPanelIndex,
    isMovingToSameSection,
    isMovingToSamePosition,
    activeSection,
    targetSection,
  } = getMovePanelData(state, active, target);

  if (activePanelIndex === -1 || targetPanelIndex === -1) {
    // Validate that both panels exist
    console.warn('movePanelAction: Panel not found', {
      activePanelRefId: active.panelRef.id,
      targetPanelRefId: target.panelRef?.id,
      activeIndex: activePanelIndex,
      targetIndex: targetPanelIndex,
    });
    return [state, Actions.noOp(ref)];
  }

  if (isMovingToSameSection && isMovingToSamePosition) {
    // If moving to the same section and position, no-op
    return [state, Actions.noOp(ref)];
  }

  let newState: ViewReducerState;

  const activeData = {
    panelIndex: activePanelIndex,
    panelRef: active.panelRef,
    sectionRef: active.sectionRef,
    section: activeSection,
  };

  const targetData = {
    panelIndex: targetPanelIndex,
    insertDir: target.insertDir,
    panelRef: target.panelRef,
    sectionRef: target.sectionRef,
    section: targetSection,
  };

  if (!isMovingToSameSection) {
    newState = handleMovePanelToDiffSection(state, activeData, targetData);
  } else {
    newState = handleMovePanelToSameSection(state, activeData, targetData);
  }

  newState = setInShallowClone(
    newState,
    ['parts', active.panelRef.type, active.panelRef.id, 'isAuto'],
    active.prevIsPanelAuto
  );

  newState = setInShallowClone(
    newState,
    ['parts', active.sectionRef.type, active.sectionRef.id, 'isPanelsAuto'],
    active.prevSectionPanelsAuto
  );

  if (!isMovingToSameSection) {
    newState = setInShallowClone(
      newState,
      ['parts', target.sectionRef.type, target.sectionRef.id, 'isPanelsAuto'],
      target.prevSectionPanelsAuto
    );
  }

  const {undoTargetPanelRef, undoInsertDir} = getUndoData(newState, activeData);

  const redoAction = Actions.movePanel2(
    ref,
    {
      panelRef: active.panelRef,
      sectionRef: target.sectionRef, // the section it got moved to
    },
    {
      panelRef: undoTargetPanelRef,
      sectionRef: active.sectionRef,
      insertDir: undoInsertDir,
    }
  );

  return [newState, redoAction];
};
